<script>
import bem from '@/utils/bem';
import { mapActions } from 'vuex';
import ArtFrame from '@/components/modals/Art-frame.vue';
import {
  PlusOutlined,
  DeleteOutlined,
  PictureOutlined,
  StarFilled,
} from '@ant-design/icons-vue';

const b = bem('frames-grid');

export default {
  components: {
    ArtFrame,
    PlusOutlined,
    DeleteOutlined,
    PictureOutlined,
    StarFilled,
  },
  props: {
    frames: { type: Array, default: () => [] },
    collection: { type: Object, default: () => ({}) },
  },
  emits: ['select', 'updated'],
  data() {
    return {
      showModal: false,
    };
  },
  methods: {
    ...mapActions({
      deleteAction: 'createTemplate/deleteTemplateFrame',
    }),
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    onCardClick(frame) {
      this.$emit('select', frame);
    },
    onCreate() {
      this.showModal = true;
    },
    async onDelete(e, frame) {
      e.stopPropagation();
      await this.deleteAction({
        id: frame.id,
        name: frame.name,
      });
      this.$emit('updated');
    },
    // Build composite preview from first option's first display.
    // Uses mobileDisplay layer (900x675 canvas) for card proportions.
    // Returns: background, frame overlay position, and photo cutout
    // position (frameImageOptions %) nested inside the frame.
    getPreview(frame) {
      const display = frame.options?.[0]?.frameDisplays?.[0];
      if (!display) return null;

      const layer = display.mobileDisplay || display.fullDisplay;
      if (!layer || !display.frameImageUri) return null;

      // Frame position as % of the layer
      const { frameTop, frameLeft } = layer;
      const frameHeight = layer.frameScale;
      const frameWidth = layer.frameScaleX;

      // Photo cutout inside the frame (% of frame dimensions)
      const imgOpt = display.frameImageOptions?.[0];

      return {
        background: display.baseImageUri,
        frame: display.frameImageUri,
        frameTop,
        frameLeft,
        frameHeight,
        frameWidth,
        // Photo position — absolute % of the full layer, computed from
        // frame position + frameImageOptions (% relative to frame)
        hasPhoto: !!imgOpt,
        photoTop: imgOpt ? frameTop + (imgOpt.y / 100) * frameHeight : 0,
        photoLeft: imgOpt ? frameLeft + (imgOpt.x / 100) * frameWidth : 0,
        photoHeight: imgOpt ? (imgOpt.height / 100) * frameHeight : 0,
        photoWidth: imgOpt ? (imgOpt.width / 100) * frameWidth : 0,
      };
    },
  },
};
</script>

<template>
  <div :class="b()">
    <div :class="b('header')">
      <h2 :class="b('title')">
        {{ collection.name || 'Frames' }}
      </h2>
      <a-button type="primary" @click="onCreate">
        <template #icon><PlusOutlined /></template>
        New frame
      </a-button>
    </div>

    <div :class="b('grid')">
      <div
        v-for="frame in frames"
        :key="frame.id"
        :class="b('card')"
        role="button"
        tabindex="0"
        @click="onCardClick(frame)"
        @keydown.enter="onCardClick(frame)"
      >
        <div :class="b('card-image')">
          <!-- Composite preview: background + photo inside frame + frame overlay -->
          <template v-if="getPreview(frame)">
            <img
              :class="b('card-bg')"
              :src="getPreview(frame).background"
              alt=""
            />
            <!-- Photo inside the frame cutout — uses frame.imageUri -->
            <img
              v-if="getPreview(frame).hasPhoto && frame.imageUri"
              :class="b('card-photo')"
              :src="frame.imageUri"
              alt=""
              :style="{
                top: getPreview(frame).photoTop + '%',
                left: getPreview(frame).photoLeft + '%',
                height: getPreview(frame).photoHeight + '%',
                width: getPreview(frame).photoWidth + '%',
              }"
            />
            <!-- Frame overlay on top -->
            <img
              :class="b('card-frame')"
              :src="getPreview(frame).frame"
              :alt="frame.displayName"
              :style="{
                top: getPreview(frame).frameTop + '%',
                left: getPreview(frame).frameLeft + '%',
                height: getPreview(frame).frameHeight + '%',
                width: getPreview(frame).frameWidth + '%',
              }"
            />
          </template>
          <template v-else-if="frame.imageUri">
            <img :src="frame.imageUri" :alt="frame.displayName" />
          </template>
          <div v-else :class="b('card-placeholder')">
            <PictureOutlined />
          </div>
          <a-tag
            v-if="frame.isDefault"
            :class="b('default-badge')"
            color="gold"
          >
            <StarFilled /> Default
          </a-tag>
        </div>

        <div :class="b('card-body')">
          <h3 :class="b('card-title')">
            {{ frame.displayName || frame.name }}
          </h3>
          <span :class="b('card-type')">
            {{ frame.name === 'collage' ? 'Collage' : 'Frame' }}
          </span>
        </div>

        <div :class="b('card-footer')">
          <a-popconfirm
            title="Delete this frame?"
            ok-text="Yes"
            cancel-text="No"
            @confirm="onDelete($event, frame)"
          >
            <a-button
              type="text"
              size="small"
              danger
              @click.stop
            >
              <template #icon><DeleteOutlined /></template>
            </a-button>
          </a-popconfirm>
        </div>
      </div>
    </div>

    <a-empty
      v-if="frames.length === 0"
      description="No frames in this collection"
    />

    <ArtFrame
      v-if="showModal"
      v-model:show="showModal"
      :collection="collection"
      :is-new="true"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';

.frames-grid {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
  }

  &__title {
    font-size: 22px;
    font-weight: 700;
    color: var(--Text-Primary);
    margin: 0;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 20px;
  }

  &__card {
    background: var(--Background-Main);
    border: 1px solid var(--Border-Light);
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;

    &:hover {
      border-color: var(--Button-Primary);
      box-shadow: var(--Shadow-Medium);
      transform: translateY(-2px);
    }

    &:focus-visible {
      outline: 2px solid var(--Button-Primary);
      outline-offset: 2px;
    }
  }

  &__card-image {
    width: 100%;
    // Matches mobileDisplay canvas ratio (900x675 = 4:3)
    aspect-ratio: 900 / 675;
    overflow: hidden;
    background: var(--Background-Tertiary);
    position: relative;
  }

  // Background image — fills entire card image area
  &__card-bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  // Photo inside the frame cutout — frame.imageUri
  &__card-photo {
    position: absolute;
    object-fit: cover;
    z-index: 1;
  }

  // Frame overlay — positioned using mobileDisplay percentages, above photo
  &__card-frame {
    position: absolute;
    object-fit: contain;
    z-index: 2;
  }

  &__card-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 36px;
    color: var(--Text-Secondary);
    opacity: 0.4;
  }

  &__default-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    font-size: 11px;
  }

  &__card-body {
    padding: 12px 16px;
    flex: 1;
  }

  &__card-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--Text-Primary);
    margin: 0 0 2px;
  }

  &__card-type {
    font-size: 12px;
    color: var(--Text-Secondary);
  }

  &__card-footer {
    display: flex;
    gap: 4px;
    padding: 6px 12px;
    border-top: 1px solid var(--Border-Light);
  }
}
</style>
